/**
 * Enhanced Notification Utilities
 * Handles navigation and display logic for all notification types across the app ecosystem
 */

import {
  APP_URLS,
  getAppUrl,
  NOTIFICATION_TYPES,
  NOTIFICATION_ROUTES,
  NOTIFICATION_UI_CONFIG,
  getNotificationUIConfig,
} from '../config/notificationConfig';

/**
 * Determines the notification type from options
 * @param {Object} options - Parsed notification options
 * @returns {string} - Notification type constant
 */
export const getNotificationType = (options) => {
  if (!options) return NOTIFICATION_TYPES.GENERAL;

  // Check if notification_type is explicitly provided (new backend implementation)
  if (options.notification_type) {
    return options.notification_type;
  }

  // Legacy detection logic for backward compatibility
  // Exam notifications
  if (options.is_started || options.is_urgent) {
    return NOTIFICATION_TYPES.EXAM_STARTED;
  }
  if (options.is_reminder && options.exam_id) {
    return NOTIFICATION_TYPES.EXAM_DEADLINE_REMINDER;
  }
  if (options.results_url || (options.score !== undefined && options.is_passed !== undefined)) {
    return NOTIFICATION_TYPES.EXAM_GRADED;
  }
  if (options.correction_url || options.requires_manual_grading) {
    return NOTIFICATION_TYPES.GRADING_REQUIRED;
  }
  if (options.take_exam_url && !options.results_url) {
    return NOTIFICATION_TYPES.EXAM_PUBLISHED;
  }
  if (options.exam_url && !options.take_exam_url) {
    return NOTIFICATION_TYPES.EXAM_PUBLISHED;
  }
  if (options.status && (options.status === 'H' || options.status === 'P' || options.status === 'R')) {
    return NOTIFICATION_TYPES.COURSE_COMPLETED;
  }

  // Live session notifications
  if (options.live_link && options.meeting_code) {
    return NOTIFICATION_TYPES.LIVE_STARTED;
  }

  // Course notifications
  if (options.course_activated) {
    return NOTIFICATION_TYPES.COURSE_ACTIVATED;
  }
  if (options.lesson_activated) {
    return NOTIFICATION_TYPES.LESSON_ACTIVATED;
  }

  // Payment notifications
  if (options.payment_verified) {
    return NOTIFICATION_TYPES.PAYMENT_VERIFIED;
  }

  return NOTIFICATION_TYPES.GENERAL;
};

/**
 * Determines if a notification is exam-related
 * @param {Object} options - Parsed notification options
 * @returns {boolean}
 */
export const isExamNotification = (options) => {
  const type = getNotificationType(options);
  return [
    NOTIFICATION_TYPES.EXAM_PUBLISHED,
    NOTIFICATION_TYPES.EXAM_STARTED,
    NOTIFICATION_TYPES.EXAM_DEADLINE_REMINDER,
    NOTIFICATION_TYPES.EXAM_GRADED,
    NOTIFICATION_TYPES.EXAM_SUBMITTED,
    NOTIFICATION_TYPES.GRADING_REQUIRED,
    NOTIFICATION_TYPES.COURSE_COMPLETED,
  ].includes(type);
};

/**
 * Determines if a notification is live session-related
 * @param {Object} options - Parsed notification options
 * @returns {boolean}
 */
export const isLiveNotification = (options) => {
  const type = getNotificationType(options);
  return [
    NOTIFICATION_TYPES.LIVE_STARTED,
    NOTIFICATION_TYPES.LIVE_SCHEDULED,
    NOTIFICATION_TYPES.LIVE_ENDED,
  ].includes(type);
};

/**
 * Gets the navigation data for a notification
 * @param {Object} options - Parsed notification options
 * @param {Object} user - User object with role information
 * @returns {Object|null} - Navigation data or null
 */
export const getNotificationNavigation = (options, user) => {
  if (!options) return null;

  const notificationType = getNotificationType(options);
  const routeConfig = NOTIFICATION_ROUTES[notificationType];

  if (!routeConfig) {
    console.warn('⚠️ No route configuration found for notification type:', notificationType);
    return getLegacyNavigationFallback(options, user);
  }

  try {
    // Get the target app URL
    const appUrl = options.app_url || getAppUrl(routeConfig.app);
    
    // Get the route path
    const routePath = routeConfig.getRoute(options);
    
    // Construct full URL
    const cleanAppUrl = appUrl.replace(/\/$/, '');
    const cleanRoutePath = routePath.startsWith('/') ? routePath : `/${routePath}`;
    const fullUrl = `${cleanAppUrl}${cleanRoutePath}`;

    console.log('🔗 Notification Navigation:', {
      type: notificationType,
      app: routeConfig.app,
      appUrl: cleanAppUrl,
      routePath: cleanRoutePath,
      fullUrl: fullUrl,
      external: routeConfig.external,
    });

    return {
      type: notificationType,
      app: routeConfig.app,
      url: routePath,
      appUrl: cleanAppUrl,
      fullUrl: fullUrl,
      external: routeConfig.external,
      options: options, // Pass along for additional context
    };
  } catch (error) {
    console.error('❌ Error generating notification navigation:', error);
    return getLegacyNavigationFallback(options, user);
  }
};

/**
 * Legacy fallback navigation for backward compatibility
 * @param {Object} options - Parsed notification options
 * @param {Object} user - User object
 * @returns {Object|null}
 */
const getLegacyNavigationFallback = (options, user) => {
  console.warn('⚠️ Using legacy navigation fallback');

  // Legacy exam navigation
  if (isExamNotification(options)) {
    const baseUrl = options.app_url || getAppUrl('EXAMS');
    let targetUrl = '/';

    if (options.correction_url && (user?.isInstructor || user?.isSuperAdmin)) {
      targetUrl = options.correction_url;
    } else if (options.take_exam_url) {
      targetUrl = options.take_exam_url;
    } else if (options.results_url) {
      targetUrl = options.results_url;
    } else if (options.exam_url) {
      targetUrl = options.exam_url;
    } else if (options.scores_url) {
      targetUrl = options.scores_url;
    }

    const cleanUrl = targetUrl.startsWith('/') ? targetUrl : `/${targetUrl}`;
    return {
      type: NOTIFICATION_TYPES.GENERAL,
      app: 'EXAMS',
      url: cleanUrl,
      appUrl: baseUrl.replace(/\/$/, ''),
      fullUrl: `${baseUrl.replace(/\/$/, '')}${cleanUrl}`,
      external: true,
      options: options,
    };
  }

  // Legacy live navigation
  if (isLiveNotification(options) && options.live_link) {
    let url = options.live_link;
    let fullUrl = options.live_link;
    
    // If live_link is a full URL, extract the path for url
    try {
      const parsedUrl = new URL(options.live_link);
      url = parsedUrl.pathname + parsedUrl.search;
      fullUrl = options.live_link;
    } catch (e) {
      // It's already a path, construct full URL
      const baseUrl = options.app_url || getAppUrl('STREAM');
      fullUrl = `${baseUrl.replace(/\/$/, '')}${url.startsWith('/') ? url : '/' + url}`;
    }
    
    return {
      type: NOTIFICATION_TYPES.LIVE_STARTED,
      app: 'STREAM',
      url: url,
      appUrl: options.app_url || getAppUrl('STREAM'),
      fullUrl: fullUrl,
      external: true,
      options: options,
    };
  }

  return null;
};

/**
 * Handles navigation for notifications
 * @param {Object} navigationData - Result from getNotificationNavigation
 * @param {Function} navigate - React Router navigate function (for internal routes)
 * @param {boolean} openInNewTab - Whether to open in a new tab (default: true)
 */
export const handleNotificationNavigation = (navigationData, navigate = null, openInNewTab = true) => {
  if (!navigationData) {
    console.warn('⚠️ No navigation data provided');
    return;
  }

  if (navigationData.external) {
    // External navigation (to different app) - always open in new tab
    console.log('🚀 External navigation to:', navigationData.fullUrl);
    window.open(navigationData.fullUrl, '_blank', 'noopener,noreferrer');
  } else {
    // Internal navigation (same app)
    if (openInNewTab) {
      // Open internal route in new tab
      const currentOrigin = window.location.origin;
      const fullUrl = `${currentOrigin}${navigationData.url}`;
      console.log('🔄 Internal navigation (new tab) to:', fullUrl);
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Navigate within current tab (requires navigate function)
      if (!navigate) {
        console.error('❌ Navigate function not provided for internal navigation');
        return;
      }
      
      console.log('🔄 Internal navigation to:', navigationData.url);
      navigate(navigationData.url, {
        state: navigationData.options || {},
      });
    }
  }
};

/**
 * Gets display configuration for a notification
 * @param {Object} options - Parsed notification options
 * @param {Object} user - User object
 * @returns {Object} - Display configuration
 */
export const getNotificationDisplay = (options, user = null) => {
  const notificationType = getNotificationType(options);
  const baseConfig = getNotificationUIConfig(notificationType);

  // Add score information if available (for exam results)
  const display = { ...baseConfig };

  if (options.score !== undefined && options.max_score !== undefined) {
    display.scoreInfo = `${options.score}/${options.max_score}`;
    
    if (options.percentage !== undefined) {
      display.percentage = `${options.percentage}%`;
    }

    // Determine pass/fail status
    if (options.is_passed !== undefined) {
      display.isPassed = options.is_passed;
      display.passStatus = options.is_passed ? 'Passed' : 'Failed';
    }
  }

  // Add deadline information if available
  if (options.deadline) {
    display.deadline = options.deadline;
  }

  if (options.hours_remaining !== undefined) {
    display.hoursRemaining = options.hours_remaining;
  }

  // Add course status for course completion
  if (options.status && (options.status_description_en || options.status_description_ar)) {
    display.statusEn = options.status_description_en;
    display.statusAr = options.status_description_ar;
  }

  return display;
};

/**
 * Legacy function - kept for backward compatibility
 * @deprecated Use getNotificationDisplay instead
 */
export const getExamNotificationDisplay = (options) => {
  console.warn('⚠️ getExamNotificationDisplay is deprecated. Use getNotificationDisplay instead.');
  return getNotificationDisplay(options);
};

/**
 * Determines if a notification should be marked as urgent/priority
 * @param {Object} options - Parsed notification options
 * @returns {boolean}
 */
export const isUrgentNotification = (options) => {
  const notificationType = getNotificationType(options);
  const config = getNotificationUIConfig(notificationType);
  return config.isUrgent || false;
};

/**
 * Gets the appropriate icon for a notification
 * @param {Object} options - Parsed notification options
 * @returns {string} - Emoji or icon
 */
export const getNotificationIcon = (options) => {
  const notificationType = getNotificationType(options);
  const config = getNotificationUIConfig(notificationType);
  return config.icon || '🔔';
};

/**
 * Gets the action button text for a notification
 * @param {Object} options - Parsed notification options
 * @returns {string} - Button text
 */
export const getNotificationActionText = (options) => {
  const notificationType = getNotificationType(options);
  const config = getNotificationUIConfig(notificationType);
  return config.actionText || 'View';
};
